package tools

import AppBuildConfig
import androidx.compose.runtime.*
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextDecoration
import api.AppBehavior
import api.DayOfWeek
import api.LicenseState
import api.ResourceType
import com.mohamedrejeb.richeditor.model.RichTextState
import com.mohamedrejeb.richeditor.model.rememberRichTextState
import kotlinx.datetime.Month
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.tools.calculateAspectRatio
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.Navigation
import kotlin.math.pow
import kotlin.math.roundToLong

@Composable
fun roundBytes(bytes: Long, default: @Composable () -> String) = bytes.roundBytes() ?: default()

fun Long.roundBytes(): String? = when {
    this < 1 -> null
    this >= 1_000_000_000_000 -> "${this.format(1_000_000_000_000, 2)} TB"
    this >= 1_000_000_000 -> "${this.format(1_000_000_000, 2)} GB"
    this >= 1_000_000 -> "${this.format(1_000_000, 2)} MB"
    this >= 1_000 -> "${this.format(1_000, 2)} KB"
    else -> "$$this bytes"
}

fun Number.roundDecimals(decimals: Int): String {
    return if (decimals == 0 || toDouble() == 0.0) this.toLong().toString()
    else {
        val pow = 10.0.pow(decimals).roundToLong()
        (toDouble() * pow.toDouble())
            .roundToLong()
            .let {
                val entry = it.toString()
                buildString {
                    append(entry.substring(0, entry.length - decimals))
                    if (it % pow != 0L) {
                        append(".")
                        append(entry.substring(entry.length - decimals, entry.length))
                    }
                }
            }
    }
}

fun Number.format(divider: Number, decimals: Int): String {
    val left = this.toDouble() % divider.toDouble()
    val dividerLength = divider.toLong().toString().length - 1
    return buildString {
        append(((this@format.toLong() - left) / (divider.toLong())).roundToLong())
        if (left > 0 && decimals > 0) {
            append('.')
            append(left.toString().padStart(dividerLength, '0').take(decimals))
        }
    }
}

@Composable
fun rememberAspectRatio(width: Int, height: Int): Pair<Int, Int> {
    var result by remember { mutableStateOf(0 to 0) }

    LaunchedEffect(width, height) {
        result = calculateAspectRatio(width, height)
    }

    return result
}

fun Throwable.printStackTraceInDebug() {
    if (AppBuildConfig.DEBUG) printStackTrace()
}

fun Navigation.findRoot(): Navigation {
    return parentNavigation?.findRoot() ?: this
}

val ResourceType.labelRes: StringResource
    get() = when (this) {
        ResourceType.IMAGE -> Res.string.global__resource_type__image
        ResourceType.DOCUMENT -> Res.string.global__resource_type__pdf
        ResourceType.UNDEFINED -> Res.string.global__resource_type__all
        ResourceType.VIDEO -> Res.string.global__resource_type__video
        ResourceType.WWW_APP -> Res.string.global__resource_type__www_app
        ResourceType.RAW,
        ResourceType.TEXT -> Res.string.global_empty

        ResourceType.ICON -> Res.string.global__resource_type__icon
        ResourceType.VIDEO_ALPHA -> Res.string.global__resource_type__video_alpha
        ResourceType.AUDIO -> Res.string.global__resource_type__audio
    }

fun Month.stringRes(): StringResource {
    return when (this) {
        Month.JANUARY -> Res.string.global__month_jan
        Month.FEBRUARY -> Res.string.global__month_feb
        Month.MARCH -> Res.string.global__month_mar
        Month.APRIL -> Res.string.global__month_apr
        Month.MAY -> Res.string.global__month_may
        Month.JUNE -> Res.string.global__month_jun
        Month.JULY -> Res.string.global__month_jul
        Month.AUGUST -> Res.string.global__month_aug
        Month.SEPTEMBER -> Res.string.global__month_sep
        Month.OCTOBER -> Res.string.global__month_oct
        Month.NOVEMBER -> Res.string.global__month_nov
        Month.DECEMBER -> Res.string.global__month_dec
    }
}

fun DayOfWeek.toStringRes(): StringResource {
    return when (this) {
        DayOfWeek.MONDAY -> Res.string.global__day_of_week__mon
        DayOfWeek.TUESDAY -> Res.string.global__day_of_week__tue
        DayOfWeek.WEDNESDAY -> Res.string.global__day_of_week__wen
        DayOfWeek.THURSDAY -> Res.string.global__day_of_week__thu
        DayOfWeek.FRIDAY -> Res.string.global__day_of_week__fri
        DayOfWeek.SATURDAY -> Res.string.global__day_of_week__sat
        DayOfWeek.SUNDAY -> Res.string.global__day_of_week__sun
    }
}

fun DayOfWeek.toStringResShort(): StringResource {
    return when (this) {
        DayOfWeek.MONDAY -> Res.string.global__day_of_week__short_mon
        DayOfWeek.TUESDAY -> Res.string.global__day_of_week__short_tue
        DayOfWeek.WEDNESDAY -> Res.string.global__day_of_week__short_wen
        DayOfWeek.THURSDAY -> Res.string.global__day_of_week__short_thu
        DayOfWeek.FRIDAY -> Res.string.global__day_of_week__short_fri
        DayOfWeek.SATURDAY -> Res.string.global__day_of_week__short_sat
        DayOfWeek.SUNDAY -> Res.string.global__day_of_week__short_sun
    }
}

fun LicenseState.stringRes(): StringResource {
    return when (this) {
        LicenseState.APPROVED -> Res.string.license_state_approved
        LicenseState.DECLINED -> Res.string.license_state_declined
        LicenseState.IDLE -> Res.string.license_state_idle
        LicenseState.REVIEW -> Res.string.license_state_review
        LicenseState.TERMINATED -> Res.string.license_state_terminated
    }
}

@Composable
fun rememberRichTextState(markdown: StringResource): RichTextState {
    val richTextState = rememberRichTextState()
    LaunchedEffect(markdown.key) {
        richTextState.config.linkColor = theme.primaryDark
        richTextState.config.linkTextDecoration = TextDecoration.None
        richTextState.config.codeSpanColor = Color(0xFFd7882d)
        richTextState.config.codeSpanBackgroundColor = Color.Transparent
        richTextState.config.codeSpanStrokeColor = Color(0xFF494b4d)
        richTextState.config.unorderedListIndent = 20
        richTextState.config.orderedListIndent = 25
        richTextState.setMarkdown(markdown.await())
    }
    return richTextState
}

fun AppBehavior.stringRes(): StringResource = when (this) {
    AppBehavior.CAMPAIGN_OVERLAP -> Res.string.layer_top_detail_editor__apps_playback_setup_overlap
    AppBehavior.REDUCE_IMPRESSIONS -> Res.string.layer_top_detail_editor__apps_playback_setup_reduction
}